import { useCallback } from "react";
import { ComboboxOption } from "@altimateai/lego";
import { FilterCondition } from "@/components/tables/types";
import { Datastore } from "@/pages/datastores/types";

type TableFilters = Record<string, Record<string, Record<string, Record<string, unknown>>>>;

interface UseDatasetsFilterOptionsProps {
  tableFilters?: TableFilters;
  dsType: string;
  datastores?: Datastore[];
}

/** Returns stable option-builder callbacks for dataset filter cascading. */
export function useDatasetsFilterOptions({
  tableFilters,
  dsType,
  datastores,
}: UseDatasetsFilterOptionsProps) {
  const resolveInstanceName = useCallback(
    (instanceId: string): string =>
      (datastores ?? []).find(d => String(d.instance_id) === String(instanceId))?.name ||
      instanceId,
    [datastores]
  );

  /** All instances for the current datasource type, derived from filter data. */
  const getInstanceOptions = useCallback((): ComboboxOption[] => {
    const dsFilters = tableFilters?.[dsType];
    if (!dsFilters) return [];
    return Object.keys(dsFilters).map(id => ({
      label: resolveInstanceName(id),
      value: resolveInstanceName(id),
    }));
  }, [tableFilters, dsType, resolveInstanceName]);

  /**
   * Databases for the current datasource type.
   * Filtered to selected instances when any are selected; otherwise returns all databases.
   */
  const getDatabaseOptions = useCallback(
    (activeFilters: FilterCondition[]): ComboboxOption[] => {
      const selectedInstances = activeFilters.find(f => f.field === "datasources")?.value as
        | string[]
        | undefined;
      const dsFilters = tableFilters?.[dsType];
      if (!dsFilters) return [];

      const dbSet = new Set<string>();
      const result: ComboboxOption[] = [];
      for (const instanceId in dsFilters) {
        if (
          selectedInstances?.length &&
          !selectedInstances.includes(resolveInstanceName(instanceId))
        )
          continue;
        for (const db in dsFilters[instanceId]) {
          if (!dbSet.has(db)) {
            dbSet.add(db);
            result.push({ label: db, value: db });
          }
        }
      }
      return result;
    },
    [tableFilters, dsType]
  );

  /**
   * Schemas for the current datasource type.
   * Filtered to selected databases when any are selected; otherwise returns all schemas.
   */
  const getSchemaOptions = useCallback(
    (activeFilters: FilterCondition[]): ComboboxOption[] => {
      const selectedDatabases = activeFilters.find(f => f.field === "databases")?.value as
        | string[]
        | undefined;
      const dsFilters = tableFilters?.[dsType];
      if (!dsFilters) return [];

      const schemaSet = new Set<string>();
      const result: ComboboxOption[] = [];
      for (const instanceId in dsFilters) {
        for (const db in dsFilters[instanceId]) {
          if (selectedDatabases?.length && !selectedDatabases.includes(db)) continue;
          for (const schema in dsFilters[instanceId][db]) {
            if (!schemaSet.has(schema)) {
              schemaSet.add(schema);
              result.push({ label: schema, value: schema });
            }
          }
        }
      }
      return result;
    },
    [tableFilters, dsType]
  );

  return { getInstanceOptions, getDatabaseOptions, getSchemaOptions };
}
